// @flow

import 'isomorphic-fetch'
import { push } from 'react-router-redux'
import { checkHttpStatus, getApiHeaders, getApiBaseUrlWithOrg } from '../utils'
import { basicGet } from '../utils/api'
import { setQuestSection, createMentorNote } from './quest'
import { getErrorAction } from '../utils/actions'
import type { Action } from '../types/actions/activity'
import type { APIError } from '../types'

export const REQUEST_QUEST_ACTIVITY_NOTES = 'REQUEST_QUEST_ACTIVITY_NOTES'
export const RECEIVE_QUEST_ACTIVITY_NOTES = 'RECEIVE_QUEST_ACTIVITY_NOTES'
export const FAILED_QUEST_ACTIVITY_NOTES = 'FAILED_QUEST_ACTIVITY_NOTES'
export const DELETE_ACTIVITY_SUCCESS = 'DELETE_ACTIVITY_SUCCESS'
export const EDIT_ACTIVITY_DETAILS_SUCCESS = 'EDIT_ACTIVITY_DETAILS_SUCCESS'
export const DELETE_ACTIVITY_MENTOR_NOTE_SUCCESS =
  'DELETE_ACTIVITY_MENTOR_NOTE_SUCCESS'
export const CREATE_QUEST_ACTIVITY_NOTE_SUCCESS =
  'CREATE_QUEST_ACTIVITY_NOTE_SUCCESS'
export const EDIT_QUEST_ACTIVITY_NOTE_REQUEST =
  'EDIT_QUEST_ACTIVITY_NOTE_REQUEST'
export const EDIT_QUEST_ACTIVITY_NOTE_SUCCESS =
  'EDIT_QUEST_ACTIVITY_NOTE_SUCCESS'
export const EDIT_QUEST_ACTIVITY_NOTE_FAILURE =
  'EDIT_QUEST_ACTIVITY_NOTE_FAILURE'
export const CREATE_ACTIVITY_SUCCESS = 'CREATE_ACTIVITY_SUCCESS'
export const CREATE_ACTIVITY_REQUEST = 'CREATE_ACTIVITY_REQUEST'
export const CREATE_ACTIVITY_FAILURE = 'CREATE_ACTIVITY_FAILURE'
export const EDIT_ACTIVITY_POSITION_SUCCESS = 'EDIT_ACTIVITY_POSITION_SUCCESS'
export const EDIT_ACTIVITY_DETAILS_REQUEST = 'EDIT_ACTIVITY_DETAILS_REQUEST'
export const EDIT_ACTIVITY_DETAILS_FAILURE = 'EDIT_ACTIVITY_DETAILS_FAILURE'
export const REQUEST_ACTIVITY_READABLE_STATS = 'REQUEST_ACTIVITY_READABLE_STATS'
export const RECEIVE_ACTIVITY_READABLE_STATS = 'RECEIVE_ACTIVITY_READABLE_STATS'
export const FAILED_ACTIVITY_READABLE_STATS = 'FAILED_ACTIVITY_READABLE_STATS'
export const EDIT_QUEST_ACTIVITY_NOTE_POSITION_SUCCESS =
  'EDIT_QUEST_ACTIVITY_NOTE_POSITION_SUCCESS'

export function requestQuestActivityNotes(): Action {
  return {
    type: REQUEST_QUEST_ACTIVITY_NOTES
  }
}

export function receiveQuestActivityNotes(response: Object): Action {
  return {
    type: RECEIVE_QUEST_ACTIVITY_NOTES,
    notes: response.activity_notes
  }
}

export function editActivityDetailsFailure(error: APIError): Action {
  return getErrorAction(EDIT_ACTIVITY_DETAILS_FAILURE, error)
}

export function editActivityDetailsRequest(): Action {
  return {
    type: EDIT_ACTIVITY_DETAILS_REQUEST
  }
}

export function editActivityPositionSuccess(
  activityId: number,
  newIndex: number
): Action {
  return {
    type: EDIT_ACTIVITY_POSITION_SUCCESS,
    payload: {
      activityId: activityId,
      newIndex: newIndex
    }
  }
}

export function createActivityFailure(error: APIError): Action {
  return getErrorAction(CREATE_ACTIVITY_FAILURE, error)
}

export function createActivityRequest(): Action {
  return {
    type: CREATE_ACTIVITY_REQUEST
  }
}

export function createActivitySuccess(response: Object): Action {
  return {
    type: CREATE_ACTIVITY_SUCCESS,
    activity: response
  }
}

export function editQuestActivityNotePositionSuccess(
  response: Object,
  newIndex: number
): Action {
  return {
    type: EDIT_QUEST_ACTIVITY_NOTE_POSITION_SUCCESS,
    note: response,
    newIndex: newIndex
  }
}

export function editQuestActivityNoteFailure(error: APIError): Action {
  return getErrorAction(EDIT_QUEST_ACTIVITY_NOTE_FAILURE, error)
}

export function editQuestActivityNoteSuccess(response: Object): Action {
  return {
    type: EDIT_QUEST_ACTIVITY_NOTE_SUCCESS,
    note: response
  }
}

export function editQuestActivityNoteRequest(): Action {
  return {
    type: EDIT_QUEST_ACTIVITY_NOTE_REQUEST
  }
}

export function createQuestActivityNoteSuccess(response: Object): Action {
  return {
    type: CREATE_QUEST_ACTIVITY_NOTE_SUCCESS,
    note: response
  }
}

export function deleteActivityMentorNoteSuccess(noteId: number): Action {
  return {
    type: DELETE_ACTIVITY_MENTOR_NOTE_SUCCESS,
    noteId: noteId
  }
}

export function editActivityDetailsSuccess(
  activityId: number,
  activity: Object
): Action {
  return {
    type: EDIT_ACTIVITY_DETAILS_SUCCESS,
    activityId: activityId,
    activity: activity
  }
}

export function deleteActivitySuccess(activityId: number): Action {
  return {
    type: DELETE_ACTIVITY_SUCCESS,
    activityId: activityId
  }
}

export function failedQuestActivityNotes(error: APIError): Action {
  return getErrorAction(FAILED_QUEST_ACTIVITY_NOTES, error)
}

export function requestActivityReadableStats() {
  return {
    type: REQUEST_ACTIVITY_READABLE_STATS
  }
}

export function receiveActivityReadableStats(response) {
  return {
    type: RECEIVE_ACTIVITY_READABLE_STATS,
    payload: {
      stats: response
    }
  }
}

export function fetchQuestActivityNotes(id: number): Function {
  return (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${id}/activities/${state.quest.activityId}/notes`
    return basicGet(
      state,
      dispatch,
      url,
      requestQuestActivityNotes,
      receiveQuestActivityNotes,
      failedQuestActivityNotes
    )
  }
}

export function deleteActivity(questId: number, activityId: number): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}`
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'DELETE',
          headers: getApiHeaders(state)
        })
      )
      await dispatch(deleteActivitySuccess(activityId))
      const newState = getState()
      if (!newState.quest.activityId) {
        dispatch(push(`/quest/${questId}/activities`))
      } else {
        dispatch(
          push(`/quest/${questId}/activities/${newState.quest.activityId}`)
        )
      }
    } catch (error) {
      console.log(error)
    }
  }
}

export function editActivityDetails(
  questId: number,
  activityId: number,
  activity: Object
): Function {
  return async (dispatch, getState) => {
    dispatch(editActivityDetailsRequest())
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}`
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            activity: activity
          })
        })
      )
      dispatch(editActivityDetailsSuccess(activityId, activity))
      dispatch(push(`/quest/${questId}/activities/${activityId}`))
    } catch (error) {
      dispatch(editActivityDetailsFailure(error))
    }
  }
}

export function deleteActivityMentorNote(
  questId: number,
  activityId: number,
  noteId: number
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}/notes/${noteId}`
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'DELETE',
          headers: getApiHeaders(state)
        })
      )
      dispatch(deleteActivityMentorNoteSuccess(noteId))
    } catch (error) {
      console.log(error)
    }
  }
}

export function createQuestActivityNote(
  questId: number,
  activityId: number,
  note: Object
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}/notes`
    dispatch(createMentorNote())
    let data = new FormData()
    data.append('note[description]', note.description || '')
    data.append('note[attachment]', note.file)
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(createQuestActivityNoteSuccess(json))
    } catch (error) {
      console.log(error)
    }
  }
}

export function editQuestActivityNote(
  questId: number,
  activityId: number,
  noteId: number,
  description: string
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}/notes/${noteId}`
    dispatch(editQuestActivityNoteRequest())
    let data = new FormData()
    data.append('note[description]', description)
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(editQuestActivityNoteSuccess(json))
    } catch (error) {
      dispatch(editQuestActivityNoteFailure(error))
    }
  }
}

export function editQuestActivityNotePosition(
  questId: number,
  activityId: number,
  noteId: number,
  newIndex: number
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}/notes/${noteId}`
    let data = new FormData()
    data.append('note[sort_order_position]', newIndex.toString())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(editQuestActivityNotePositionSuccess(json, newIndex))
    } catch (error) {
      console.log(error)
    }
  }
}

export function createActivity(
  questId: number,
  activityName: string
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities`
    dispatch(createActivityRequest())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'POST',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            name: activityName,
            quest_id: questId
          })
        })
      )
      const json = await response.json()
      dispatch(createActivitySuccess(json))
      dispatch(setQuestSection('activities', json.id))
      dispatch(push(`/quest/${questId}/activities/${json.id}/edit`))
    } catch (error) {
      dispatch(createActivityFailure(error))
      window.alert('There was a problem creating the activity.')
    }
  }
}

export function editActivityPosition(
  questId: number,
  activityId: number,
  newIndex: number
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}`
    let data = new FormData()
    data.append('activity[activity_order_position]', newIndex.toString())
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      dispatch(editActivityPositionSuccess(activityId, newIndex))
    } catch (error) {
      console.log(error)
    }
  }
}

export function fetchActivityReadableStats(questId, activityId) {
  return (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities/${activityId}/readable_stats`
    return basicGet(
      state,
      dispatch,
      url,
      requestActivityReadableStats,
      receiveActivityReadableStats
    )
  }
}



// WEBPACK FOOTER //
// ./src/actions/activity.js